import { useContext, useEffect, useState, ReactElement } from "react";

import { Button } from "react-bulma-components";

import "./CommutingRouteDialog.css";
import { getPreparedRoute } from "../../api/prepared_routes";
import { AuthContext } from "../../providers/CognitoAuthProvider";

import { TransitsTable } from "components/TransitsTable/TransitsTable";
import { slashedYMDAndDayOfWeek } from "helpers/date";
import { PreparedRoute } from "models/preparedRoute";
import { CommutingExpense } from "models/travelExpense";

const renderSelectButton = (
  route: PreparedRoute,
  commutingRouteProp: CommutingRouteProp,
  handleCommutingRouteSelected: (
    route_: PreparedRoute,
    at: string,
    isReturnPath: boolean
  ) => void,
  hasOutwardPath: boolean,
  hasReturnPath: boolean,
  isReturnPath: boolean
) => {
  if (commutingRouteProp.movementLogId) {
    return (
      <div className="cw-commuting-route-dialog-header-transits-actions">
        <Button
          onClick={() => {
            handleCommutingRouteSelected(
              route,
              commutingRouteProp.at,
              isReturnPath
            );
          }}
        >
          選択
        </Button>
      </div>
    );
  }
  return (
    <div className="buttons has-addons cw-commuting-route-dialog-header-transits-actions">
      <Button
        disabled={hasOutwardPath}
        onClick={() => {
          handleCommutingRouteSelected(route, commutingRouteProp.at, false);
        }}
      >
        往路選択
      </Button>
      <Button
        disabled={hasReturnPath}
        onClick={() => {
          handleCommutingRouteSelected(route, commutingRouteProp.at, true);
        }}
      >
        復路選択
      </Button>
    </div>
  );
};
const renderDialog = (
  handleClose: () => void,
  handleCommutingRouteSelected: (
    route: PreparedRoute,
    at: string,
    isReturnPath: boolean
  ) => void,
  handleChangeWorkplace: (commutingRouteProp: CommutingRouteProp) => void,
  handleNewRoute: () => void,
  isVisible: boolean,
  commutingRouteProp: CommutingRouteProp,
  routes: PreparedRoute[],
  hasOutwardPath: boolean,
  hasReturnPath: boolean,
  isReturnPath: boolean
) => {
  const commutingRouteAt = slashedYMDAndDayOfWeek(
    new Date(commutingRouteProp.at)
  );

  return (
    <div className={`modal ${isVisible ? "is-active" : ""}`}>
      <div className="modal-background" />
      <div className="modal-card cw-commuting-route-dialog-modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">
            {`${commutingRouteAt} 通勤経路変更`}
          </p>
          <button
            type="button"
            className="delete"
            aria-label="close"
            onClick={(_event) => handleClose()}
          />
        </header>
        <section className="modal-card-body">
          <div className="cw-commuting-route-dialog-header">
            <h4 className="title is-4 cw-commuting-route-dialog-header-title">
              通勤先：
              {commutingRouteProp.arrivalName
                ? commutingRouteProp.arrivalName
                : ""}
            </h4>
            <div className="cw-commuting-route-dialog-header-actions">
              <Button onClick={() => handleChangeWorkplace(commutingRouteProp)}>
                変更
              </Button>
            </div>
          </div>
          <div>
            {routes.map((route) => (
              <div key={route.id}>
                <div className="cw-commuting-route-dialog-transits-header">
                  <h5 className="title is-5 cw-commuting-route-dialog-transits-header-title">
                    {route.departureName} ~{route.arrivalName}
                  </h5>
                  {renderSelectButton(
                    route,
                    commutingRouteProp,
                    handleCommutingRouteSelected,
                    hasOutwardPath,
                    hasReturnPath,
                    isReturnPath
                  )}
                </div>
                <TransitsTable
                  preparedRouteDetails={route.preparedRouteDetails}
                />
              </div>
            ))}
          </div>
        </section>
        <footer className="modal-card-foot">
          <Button onClick={() => handleClose()}>キャンセル</Button>
          <Button onClick={() => handleNewRoute()}>新規経路追加</Button>
        </footer>
      </div>
    </div>
  );
};

export interface CommutingRouteProp {
  movementLogId: number;
  at: string;
  arrivalId: number;
  arrivalName?: string;
  departureId: number;
}

export interface CommutingRouteDialogProps {
  toggleDialog: (commutingExpense?: CommutingExpense) => void;
  registerRoute: (
    route: PreparedRoute,
    at: string,
    isReturnPath: boolean
  ) => void;
  toggleWorkplaceDialog: (commutingRouteProp: CommutingRouteProp) => void;
  isVisible: boolean;
  commutingRouteProp: CommutingRouteProp;
  hasOutwardPath: boolean;
  hasReturnPath: boolean;
  isReturnPath: boolean;
}

export function CommutingRouteDialog({
  toggleDialog,
  registerRoute,
  toggleWorkplaceDialog,
  isVisible,
  commutingRouteProp,
  hasOutwardPath,
  hasReturnPath,
  isReturnPath,
}: CommutingRouteDialogProps): ReactElement | null {
  const auth = useContext(AuthContext);
  const [routes, setRoutes] = useState<PreparedRoute[]>([]);

  useEffect(() => {
    (async () => {
      let preparedRoute = await getPreparedRoute(auth, commutingRouteProp.at);

      preparedRoute = preparedRoute.filter(
        // 勤務先が一致のものを取得する
        (item) =>
          item.arrivalId === commutingRouteProp.arrivalId ||
          item.departureId === commutingRouteProp.arrivalId
      );
      setRoutes(preparedRoute);
    })();
  }, [
    auth,
    commutingRouteProp.arrivalId,
    commutingRouteProp.at,
    commutingRouteProp.departureId,
  ]);

  const initState = () => {
    // do nothing
  };
  const handleClose = () => {
    toggleDialog({} as CommutingExpense);
    initState();
  };
  const handleCommutingRouteSelected = (
    route: PreparedRoute,
    at: string,
    isReturnPath_: boolean
  ) => {
    registerRoute(route, at, isReturnPath_);
    initState();
  };
  const handleChangeWorkplace = (commutingRouteProp_: CommutingRouteProp) => {
    toggleDialog();
    toggleWorkplaceDialog(commutingRouteProp_);
  };
  const handleNewRoute = () => {
    window.location.href = "/routes/";
  };

  return (
    <>
      {renderDialog(
        handleClose,
        handleCommutingRouteSelected,
        handleChangeWorkplace,
        handleNewRoute,
        isVisible,
        commutingRouteProp,
        routes,
        hasOutwardPath,
        hasReturnPath,
        isReturnPath
      )}
    </>
  );
}
